import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { apiPost, getErrorMessage } from '../../utils/api'
import { toast } from '../shared/NotificationToast'

/**
 * Turn a message into a Feature Request.
 *
 * Opened from the message-bubble hover action: shows the message text in an
 * editable box; on submit it's stored server-side, pushed to the CRM as a
 * deal activity when the chat's company matches a deal, and made available
 * to the Dev Mode side (its UI lands later).
 */
export const FeatureRequestModal: React.FC<{
  originalText: string
  sourceKind: 'chat' | 'ticket'
  sourceId: string | number
  messageId?: string | number
  onClose: () => void
}> = ({ originalText, sourceKind, sourceId, messageId, onClose }) => {
  const [title, setTitle] = useState('')
  const [text, setText] = useState(originalText)
  const [busy, setBusy] = useState(false)
  const titleRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    // Start on the title — it's the thing the agent sets before creating.
    titleRef.current?.focus()
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const submit = async () => {
    const trimmed = text.trim()
    if (!trimmed || busy) return
    setBusy(true)
    try {
      const resp = await apiPost<any>('/feature-requests', {
        title: title.trim() || undefined,
        text: trimmed,
        original_text: originalText,
        source_kind: sourceKind,
        source_id: String(sourceId),
        message_id: messageId != null ? String(messageId) : undefined,
      })
      toast.success(
        'Feature request created',
        resp?.pushed_to_board
          ? (resp?.pushed_to_crm
              ? 'Sent to the Dev Board (Needs Review) and the CRM deal.'
              : 'Sent to the Dev Board (Needs Review).')
          : (resp?.pushed_to_crm ? 'Logged and pushed to the CRM deal.' : 'Logged.')
      )
      onClose()
    } catch (err) {
      toast.error('Could not create feature request', getErrorMessage(err, 'Please try again'))
      setBusy(false)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="w-full max-w-lg rounded-xl bg-surface border border-border shadow-float p-5">
        <h3 className="text-base font-semibold text-txt-primary">Turn into a feature request</h3>
        <p className="text-sm text-txt-secondary mt-1">
          Give it a short title — it will be sent to the Dev Board (Needs Review) and logged against the CRM deal.
        </p>
        <label className="block mt-4 text-xs font-semibold uppercase tracking-wide text-txt-secondary">Card title</label>
        <input
          ref={titleRef}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={120}
          className="mt-1 w-full rounded-lg border border-border bg-surface-secondary px-3 py-2 text-sm text-txt-primary outline-none focus:ring-2 focus:ring-brand-500/40"
          placeholder="e.g. Export purchase orders to CSV"
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit() } }}
        />
        <label className="block mt-4 text-xs font-semibold uppercase tracking-wide text-txt-secondary">Description</label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          className="mt-1 w-full rounded-lg border border-border bg-surface-secondary px-3 py-2.5 text-sm text-txt-primary outline-none focus:ring-2 focus:ring-brand-500/40 resize-y"
          placeholder="Describe the feature request…"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-3.5 py-1.5 rounded-lg text-sm font-medium text-txt-secondary hover:bg-surface-hover transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || !text.trim()}
            className="px-3.5 py-1.5 rounded-lg text-sm font-bold text-white bg-brand-600 hover:bg-brand-700 transition-colors disabled:opacity-50"
          >
            {busy ? 'Creating…' : 'Create feature request'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
